import { ArrowRight, Globe, Key, Search, X } from "lucide-react";
import * as React from "react";

import type { InputType } from "@/entities/projects/types";
import { cn } from "@/shared/lib/cn";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { RegionCombobox } from "@/shared/ui/region-combobox";

export interface Step1Value {
  input_type: InputType;
  input_value: string;
  region_id: number;
  exclude_terms: string[];
  name: string;
}

export interface Step1InputProps {
  value: Step1Value;
  onChange: (v: Step1Value) => void;
  onNext: () => void;
}

const TYPES: {
  id: InputType;
  label: string;
  hint: string;
  placeholder: string;
  icon: React.ComponentType<{ className?: string }>;
}[] = [
  {
    id: "query",
    label: "Поисковый запрос",
    hint: "Конкретный запрос пользователя — соберём всё семантическое окружение",
    placeholder: "например: вайб-кодинг",
    icon: Search,
  },
  {
    id: "site",
    label: "Сайт",
    hint: "URL — соберём для него релевантные темы",
    placeholder: "https://example.com",
    icon: Globe,
  },
  {
    id: "niche",
    label: "Ниша",
    hint: "Широкая тема — соберём 50+ базовых запросов аудитории",
    placeholder: "например: финансовая грамотность",
    icon: Search,
  },
];

export function Step1Input({
  value,
  onChange,
  onNext,
}: Step1InputProps): JSX.Element {
  const [excludeDraft, setExcludeDraft] = React.useState("");

  const valid = value.input_value.trim().length > 0 && value.name.trim().length > 0;

  const addExclude = (): void => {
    const v = excludeDraft.trim().toLowerCase();
    if (!v) return;
    if (value.exclude_terms.includes(v)) {
      setExcludeDraft("");
      return;
    }
    onChange({ ...value, exclude_terms: [...value.exclude_terms, v] });
    setExcludeDraft("");
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {TYPES.map((t) => {
          const Icon = t.icon;
          const active = value.input_type === t.id;
          // Тип `query` обрабатывается морфологией (pymorphy3), OpenAI
          // не нужен. niche/site требуют LLM для генерации сидов.
          const needsLLM = t.id !== "query";
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => onChange({ ...value, input_type: t.id })}
              className={cn(
                "flex flex-col gap-2 rounded-2xl border p-4 text-left transition-all",
                active
                  ? "border-purple/60 bg-gradient-to-br from-purple/[0.14] to-cyan/[0.06] shadow-glow-purple"
                  : "border-white/10 bg-white/[0.03] hover:border-white/25 hover:bg-white/[0.06]",
              )}
            >
              <div className="flex items-center gap-2">
                <div
                  className={cn(
                    "flex h-8 w-8 items-center justify-center rounded-lg",
                    active
                      ? "bg-gradient-primary text-white shadow-glow-purple"
                      : "bg-white/[0.06] text-foreground",
                  )}
                >
                  <Icon className="h-4 w-4" />
                </div>
                <span className="text-sm font-semibold text-white">{t.label}</span>
              </div>
              <p className="text-xs text-muted-foreground">{t.hint}</p>
              <span
                className={cn(
                  "mt-1 inline-flex items-center gap-1 self-start rounded-full border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider",
                  needsLLM
                    ? "border-amber/35 bg-amber/10 text-amber"
                    : "border-green/35 bg-green/10 text-green",
                )}
              >
                <Key className="h-2.5 w-2.5" />
                {needsLLM ? "Yandex + OpenAI" : "только Yandex"}
              </span>
            </button>
          );
        })}
      </div>

      <div className="space-y-2">
        <Label htmlFor="input_value">
          {TYPES.find((t) => t.id === value.input_type)?.label}
        </Label>
        <Input
          id="input_value"
          value={value.input_value}
          onChange={(e) =>
            onChange({ ...value, input_value: e.target.value })
          }
          placeholder={
            TYPES.find((t) => t.id === value.input_type)?.placeholder
          }
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="project_name">Название проекта</Label>
        <Input
          id="project_name"
          value={value.name}
          onChange={(e) => onChange({ ...value, name: e.target.value })}
          placeholder="Как назовём в списке проектов"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>Регион Wordstat</Label>
          <RegionCombobox
            value={value.region_id}
            onChange={(id) => onChange({ ...value, region_id: id })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="exclude">Исключить слова</Label>
          <div className="flex gap-2">
            <Input
              id="exclude"
              value={excludeDraft}
              onChange={(e) => setExcludeDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addExclude();
                }
              }}
              placeholder="введите и Enter"
            />
            <Button
              type="button"
              variant="outline"
              onClick={addExclude}
              className="border-white/15 bg-white/[0.04]"
            >
              +
            </Button>
          </div>
          {value.exclude_terms.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {value.exclude_terms.map((term) => (
                <span
                  key={term}
                  className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/[0.04] px-2 py-0.5 text-xs text-foreground"
                >
                  {term}
                  <button
                    type="button"
                    onClick={() =>
                      onChange({
                        ...value,
                        exclude_terms: value.exclude_terms.filter(
                          (x) => x !== term,
                        ),
                      })
                    }
                    aria-label={`убрать ${term}`}
                    className="text-muted-foreground hover:text-pink"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          type="button"
          disabled={!valid}
          onClick={onNext}
          className="bg-gradient-primary text-white shadow-glow-purple disabled:opacity-60"
        >
          Дальше <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
